// ============================================================================
// Notification Registry - single source of truth for notification presentation
// ============================================================================
// Every notification "type" is described ONCE here: its panel icon + colour
// class, its toast variant, its grouped plural label, and whether it is
// person-centric (renders initials instead of an icon).
//
// Both the notifications panel (bell dropdown) and the realtime toast service
// consume this registry, so the two can never drift. Shared helpers
// (navigation URL, mention stripping, contextual detail) live here too - they
// were previously copy-pasted across both consumers.
//
// Panel colour classes are defined in
// app/shell/notifications-panel/notifications-panel.css
// (.notifications-panel__icon--<cls>). Toast variants are defined in
// shared/toast/toast.js (VARIANT_CONFIG). Only the variants that have a
// dedicated toast style are used; everything else falls back to 'notification'.
// ============================================================================

import { truncateString } from 'shared/utils/strings.js';

/** Fallback used for any type not in the registry. */
export const DEFAULT_TYPE_CONFIG = Object.freeze({
    icon: 'fa-bell',
    cls: 'default',
    toastVariant: 'notification',
    label: 'Notifications',
    initials: false,
});

/**
 * The registry. Keys are notification.type values as written by the DB.
 *   icon          - Font Awesome class for the panel + toast fallback icon
 *   cls           - panel icon colour-class suffix (must exist in the panel CSS)
 *   toastVariant  - a VARIANT_CONFIG key in toast.js ('notification' if none dedicated)
 *   label         - plural label shown when the panel coalesces a group
 *   initials      - true for person-centric types that render an avatar instead of an icon
 */
export const NOTIFICATION_TYPES = Object.freeze({
    // --- Comments / mentions / reactions ---
    comment:                  { icon: 'fa-comment',            cls: 'comment',    toastVariant: 'comment',    label: 'Comments',              initials: true },
    mention:                  { icon: 'fa-at',                 cls: 'comment',    toastVariant: 'comment',    label: 'Mentions',              initials: true },
    reaction:                 { icon: 'fa-heart',              cls: 'reaction',   toastVariant: 'reaction',   label: 'Reactions' },

    // --- Task assignment (per-task, via changelog) ---
    task_assigned:            { icon: 'fa-user-plus',          cls: 'assigned',   toastVariant: 'assigned',   label: 'Tasks assigned' },
    task_unassigned:          { icon: 'fa-user-minus',         cls: 'unassigned', toastVariant: 'unassigned', label: 'Tasks unassigned' },
    task_bulk_assigned:       { icon: 'fa-user-plus',          cls: 'assigned',   toastVariant: 'assigned',   label: 'Tasks assigned' },
    task_bulk_unassigned:     { icon: 'fa-user-minus',         cls: 'unassigned', toastVariant: 'unassigned', label: 'Tasks unassigned' },

    // --- Task reviews ---
    review_requested:         { icon: 'fa-user-check',         cls: 'assigned',   toastVariant: 'notification', label: 'Review requests' },
    review_removed:           { icon: 'fa-user-minus',         cls: 'unassigned', toastVariant: 'notification', label: 'Review removals' },
    review_sent_back:         { icon: 'fa-rotate-left',        cls: 'rejected',   toastVariant: 'notification', label: 'Reviews sent back' },
    review_approved:          { icon: 'fa-clipboard-check',    cls: 'approved',   toastVariant: 'notification', label: 'Reviews approved' },
    review_resubmitted:       { icon: 'fa-paper-plane',        cls: 'reminder',   toastVariant: 'notification', label: 'Reviews resubmitted' },

    // --- Service default-assignee (per client_service) ---
    assignee_assigned:        { icon: 'fa-user-plus',          cls: 'assigned',   toastVariant: 'assigned',   label: 'Service assignments' },
    assignee_unassigned:      { icon: 'fa-user-minus',         cls: 'unassigned', toastVariant: 'unassigned', label: 'Service reassignments' },

    // --- Leave ---
    leave_request:            { icon: 'fa-calendar-plus',      cls: 'leave',      toastVariant: 'notification', label: 'Leave requests' },
    leave_submitted:          { icon: 'fa-calendar-plus',      cls: 'leave',      toastVariant: 'notification', label: 'Leave submitted' },
    leave_manager_approved:   { icon: 'fa-calendar-check',     cls: 'approved',   toastVariant: 'notification', label: 'Leave approved' },
    leave_approved:           { icon: 'fa-calendar-check',     cls: 'approved',   toastVariant: 'notification', label: 'Leave approved' },
    leave_hr_approved:        { icon: 'fa-calendar-check',     cls: 'approved',   toastVariant: 'notification', label: 'Leave approved' },
    leave_rejected:           { icon: 'fa-calendar-times',     cls: 'rejected',   toastVariant: 'notification', label: 'Leave rejected' },
    leave_hr_rejected:        { icon: 'fa-calendar-times',     cls: 'rejected',   toastVariant: 'notification', label: 'Leave rejected' },
    leave_cancellation_requested: { icon: 'fa-calendar-minus', cls: 'leave',      toastVariant: 'notification', label: 'Leave cancellations' },
    leave_cancellation_approved:  { icon: 'fa-calendar-check', cls: 'approved',   toastVariant: 'notification', label: 'Leave cancellations approved' },
    leave_status:             { icon: 'fa-calendar',           cls: 'leave',      toastVariant: 'notification', label: 'Leave updates' },

    // --- Overtime ---
    overtime_submitted:       { icon: 'fa-business-time',      cls: 'budget',     toastVariant: 'notification', label: 'Overtime submitted' },
    overtime_manager_approved:{ icon: 'fa-business-time',      cls: 'approved',   toastVariant: 'notification', label: 'Overtime approved' },
    overtime_paid:            { icon: 'fa-money-bill-wave',    cls: 'approved',   toastVariant: 'notification', label: 'Overtime paid' },
    overtime_rejected:        { icon: 'fa-business-time',      cls: 'rejected',   toastVariant: 'notification', label: 'Overtime rejected' },

    // --- Clients / portfolio / services ---
    client_created:           { icon: 'fa-building',           cls: 'client',     toastVariant: 'notification', label: 'Clients created' },
    client_portfolio_added:   { icon: 'fa-folder-plus',        cls: 'approved',   toastVariant: 'notification', label: 'Portfolio added' },
    client_portfolio_removed: { icon: 'fa-folder-minus',       cls: 'unassigned', toastVariant: 'notification', label: 'Portfolio removed' },
    service_added:            { icon: 'fa-briefcase',          cls: 'assigned',   toastVariant: 'notification', label: 'Services added' },
    service_updated:          { icon: 'fa-briefcase',          cls: 'client',     toastVariant: 'notification', label: 'Services updated' },
    service_removed:          { icon: 'fa-briefcase',          cls: 'rejected',   toastVariant: 'notification', label: 'Services removed' },
    retainer_allocated:       { icon: 'fa-hand-holding-usd',   cls: 'approved',   toastVariant: 'notification', label: 'Retainer allocated' },

    // --- Budget ---
    budget_submitted:         { icon: 'fa-file-invoice-dollar', cls: 'budget',    toastVariant: 'notification', label: 'Budgets submitted' },
    budget_rejected:          { icon: 'fa-file-invoice-dollar', cls: 'rejected',  toastVariant: 'notification', label: 'Budgets rejected' },
    budget_approved:          { icon: 'fa-file-invoice-dollar', cls: 'approved',  toastVariant: 'notification', label: 'Budgets approved' },
    budget_accepted:          { icon: 'fa-file-invoice-dollar', cls: 'approved',  toastVariant: 'notification', label: 'Budgets approved' },
    budget_sent_back:         { icon: 'fa-file-invoice-dollar', cls: 'budget',    toastVariant: 'notification', label: 'Budgets sent back' },
    budget_signed_off:        { icon: 'fa-file-invoice-dollar', cls: 'approved',  toastVariant: 'notification', label: 'Budgets signed off' },

    // --- Billing ---
    billing_paid:             { icon: 'fa-hand-holding-usd',   cls: 'approved',   toastVariant: 'notification', label: 'Billing paid' },
    billing:                  { icon: 'fa-file-invoice-dollar', cls: 'budget',    toastVariant: 'notification', label: 'Billing updates' },

    // --- Dev tracker ---
    dev_item_completed:       { icon: 'fa-bug',                cls: 'approved',   toastVariant: 'notification', label: 'Dev items completed' },

    // --- Stats / personal records ---
    stats_afs_record:         { icon: 'fa-bolt',               cls: 'title-unlocked', toastVariant: 'title-unlocked', label: 'Personal records' },

    // --- Misc ---
    title_unlocked:           { icon: 'fa-trophy',             cls: 'title-unlocked', toastVariant: 'title-unlocked', label: 'Titles unlocked' },
    reminder:                 { icon: 'fa-bell',               cls: 'reminder',   toastVariant: 'notification', label: 'Reminders' },
});

/**
 * Get the presentation config for a notification type, with safe fallback.
 * @param {string} type
 * @returns {{icon:string, cls:string, toastVariant:string, label:string, initials:boolean}}
 */
export function getTypeConfig(type) {
    return NOTIFICATION_TYPES[type] || DEFAULT_TYPE_CONFIG;
}

/**
 * Plural label used when the panel coalesces consecutive same-type items.
 * Falls back to the notification's own title when the type is unknown.
 * @param {string} type
 * @param {string} [fallbackTitle]
 */
export function getNotificationLabel(type, fallbackTitle = '') {
    return (NOTIFICATION_TYPES[type]?.label) || fallbackTitle || DEFAULT_TYPE_CONFIG.label;
}

/** Strip @mention markdown: @[Name](uuid) -> Name */
export function stripMentionMarkdown(text) {
    if (!text) return '';
    return text.replace(/@\[([^\]]+)\]\([^)]+\)/g, '$1');
}

/**
 * Resolve the actor's display name for person-centric types (comment/mention),
 * used to render an avatar instead of an icon. Prefers the producer-set
 * data.commenterName, falling back to parsing legacy "X mentioned ..." messages.
 * @param {Object} notification
 * @returns {string|null}
 */
export function getActorName(notification) {
    const data = notification?.data || {};
    if (data.commenterName) return data.commenterName;
    const msg = notification?.message;
    if (msg && msg.includes(' mentioned')) return msg.split(' mentioned')[0];
    return null;
}

/**
 * Build a hash navigation URL from a notification's type + data.
 * Single shared implementation used by the panel and the toast service.
 * @param {Object} notification - { type, data }
 * @returns {string|null} Hash URL (e.g. '#/task?id=...') or null when there is no target.
 */
export function getNavigationUrl(notification) {
    const { type, data = {} } = notification || {};
    if (!type) return null;

    // Preferred: the structured entity columns (set by fn_notify). Falls back to
    // the legacy data-jsonb resolution below for older rows without them.
    const entityType = notification.entity_type;
    const entityId = notification.entity_id;
    if (entityType && entityId) {
        if (entityType === 'task') {
            const commentId = data.commentId || (type === 'mention' ? data.id : '');
            const budgetTab = type.startsWith('budget_') ? '&tab=budget' : '';
            const commentParam = commentId ? `&comment=${commentId}` : '';
            return `#/task?id=${entityId}${commentParam}${budgetTab}`;
        }
        if (entityType === 'client') {
            const servicesTab = (type.includes('service') || type === 'retainer_allocated'
                || type.startsWith('assignee_')) ? '&tab=services' : '';
            return `#/client?id=${entityId}${servicesTab}`;
        }
        if (entityType === 'leave') return '#/dashboard';
        // Personal stats records land on the season board they were set on.
        if (entityType === 'stats') return '#/stats?view=season&group=afs';
    }

    const taskId = data.taskId || data.task_id;

    // Task-related (comments, mentions, assignments, reactions)
    if (taskId && /^(comment|mention|reaction|task_)/.test(type)) {
        const commentId = data.commentId || (type === 'mention' ? data.id : '');
        const commentParam = commentId ? `&comment=${commentId}` : '';
        return `#/task?id=${taskId}${commentParam}`;
    }

    // Budget-related
    if (type.startsWith('budget_')) {
        const budgetTaskId = data.taskId || data.id;
        if (budgetTaskId) return `#/task?id=${budgetTaskId}&tab=budget`;
        if (data.client_id) return `#/client?id=${data.client_id}`;
        return null;
    }

    // Reminder - navigate to context if available
    if (type === 'reminder') {
        if (data.context_type === 'task' && data.context_id) {
            return `#/task?id=${data.context_id}`;
        }
        return '#/dashboard';
    }

    // Leave / overtime -> dashboard (where the request lives)
    if (type.startsWith('leave_') || type.startsWith('overtime_')) return '#/dashboard';

    // Title unlocked -> dashboard to set it
    if (type === 'title_unlocked') return '#/dashboard';

    // Client / service / portfolio-related
    const clientId = data.clientId || data.client_id;
    if (clientId) {
        if (type.includes('service') || type === 'retainer_allocated') {
            return `#/client?id=${clientId}&tab=services`;
        }
        return `#/client?id=${clientId}`;
    }

    return null;
}

/**
 * Extract contextual detail for the panel row.
 * @param {Object} notification
 * @returns {{quote: (string|null), context: (string|null)}}
 *   quote   - comment text preview (italic, quoted style)
 *   context - task/client/service/leave context line
 */
export function getNotificationDetail(notification) {
    const { type, data = {} } = notification;
    let quote = null;
    let context = null;

    const commentText = data.commentText || data.comment_text;
    const clientName = data.clientName || data.client_name;

    // Comment / mention / reaction: comment preview + task context
    if (type === 'comment' || type === 'mention' || type === 'reaction') {
        if (commentText) {
            quote = truncateString(stripMentionMarkdown(commentText), 80);
        }
        const ctxParts = [];
        if (clientName) ctxParts.push(clientName);
        if (data.serviceName) ctxParts.push(data.serviceName);
        if (ctxParts.length) context = ctxParts.join(' · ');
        return { quote, context };
    }

    // Budget / billing: client + service context
    if (type.startsWith('budget_') || type.startsWith('billing')) {
        const parts = [];
        if (clientName) parts.push(clientName);
        if (data.serviceName) parts.push(data.serviceName);
        context = parts.length ? parts.join(' · ') : null;
        return { quote, context };
    }

    // Review: send-back comment preview + client/service context
    if (type.startsWith('review_')) {
        if (type === 'review_sent_back' && commentText) {
            quote = truncateString(stripMentionMarkdown(commentText), 80);
        }
        const parts = [];
        if (clientName) parts.push(clientName);
        if (data.serviceName) parts.push(data.serviceName);
        context = parts.length ? parts.join(' · ') : null;
        return { quote, context };
    }

    // Leave: leave type + days + date range
    if (type.startsWith('leave_')) {
        const parts = [];
        if (data.leave_type) parts.push(data.leave_type);
        if (data.total_days != null) parts.push(`${data.total_days} day${data.total_days !== 1 ? 's' : ''}`);
        if (data.date_range) parts.push(data.date_range);
        context = parts.length ? parts.join(' · ') : null;
        return { quote, context };
    }

    // Overtime: claimed days + hours
    if (type.startsWith('overtime_')) {
        const parts = [];
        if (data.days_label) parts.push(data.days_label);
        if (data.hours_claimed != null) parts.push(`${data.hours_claimed}h`);
        context = parts.length ? parts.join(' · ') : null;
        return { quote, context };
    }

    // Title unlocked: title name
    if (type === 'title_unlocked') {
        context = data.title_name || null;
        return { quote, context };
    }

    return { quote, context };
}
